import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { withStorageSync, withSessionStorage } from '@angular-architects/ngrx-toolkit';
import { AuthOtpService } from './auth-otp.service';
import { Observable, filter, map, of, pipe, switchMap, tap } from 'rxjs';
import { UserInfo } from '../models/user-info.model';

export const AUTH_OTP_STORAGE_KEY = 'auth-otp-storage';

type AuthOtpState = {
  email: string;
  redirectUrl: string;
  isAuthenticated: boolean;
  userInfo: UserInfo | null;
  userInfoLoaded: boolean;
  isUserInfoLoading: boolean;
};

const initialState: AuthOtpState = {
  email: '',
  redirectUrl: '/',
  isAuthenticated: false,
  userInfo: null,
  userInfoLoaded: false,
  isUserInfoLoading: false,
};

export const AuthOtpStore = signalStore(
  { providedIn: 'root' },
  withStorageSync(
    {
      key: AUTH_OTP_STORAGE_KEY,
      select: (state: Partial<AuthOtpState>) => ({
        email: state.email,
        redirectUrl: state.redirectUrl,
      }),
      parse: (stateString: string) => JSON.parse(stateString),
    },
    withSessionStorage(),
  ),
  withState(initialState),
  withMethods((store, authService = inject(AuthOtpService)) => ({
    setEmail(email: string): void {
      patchState(store, { email: email.trim().toLowerCase() });
    },

    setRedirectUrl(redirectUrl: string): void {
      patchState(store, { redirectUrl: redirectUrl || '/' });
    },

    setFlowContext(email: string, redirectUrl: string): void {
      patchState(store, {
        email: email.trim().toLowerCase(),
        redirectUrl: redirectUrl || '/',
      });
    },

    signIn(email: string, redirectUrl?: string): Observable<boolean> {
      patchState(store, {
        email: email.trim().toLowerCase(),
        redirectUrl: redirectUrl || '/',
        userInfo: null,
        userInfoLoaded: false,
        isUserInfoLoading: false,
      });

      return authService.signIn(email).pipe(map((response) => response === true));
    },

    verify(otp: string): Observable<boolean> {
      const email = store.email();
      const redirectUrl = store.redirectUrl() || '/';

      if (!email) {
        return of(false);
      }

      return authService.verify(email, otp, redirectUrl).pipe(
        map((response) => response === true),
        tap((isAuthenticated) => {
          if (isAuthenticated) {
            patchState(store, { isAuthenticated: true });
          }
        }),
      );
    },

    loadUserInfo: rxMethod<boolean>(
      pipe(
        filter((shouldLoad) => shouldLoad && !store.userInfoLoaded() && !store.isUserInfoLoading()),
        tap(() => patchState(store, { isUserInfoLoading: true })),
        switchMap(() =>
          authService.getUserInfo().pipe(
            tapResponse({
              next: (userInfo) => {
                patchState(store, { userInfo, userInfoLoaded: true, isUserInfoLoading: false });
              },
              error: () => {
                patchState(store, { userInfo: null, userInfoLoaded: true, isUserInfoLoading: false });
              },
            }),
          ),
        ),
      ),
    ),

    clearFlow(): void {
      patchState(store, initialState);
    },
  })),
);
